import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { useId, useState, type ReactNode } from 'react';

import { cn } from '../../lib/utils';

/**
 * Building blocks for detail-page tabs (dapps, publishers). The surface tokens
 * (`bg-white/[0.02]`, `border-white/5`, uppercase micro labels) match
 * `detailParts` so a tab reads as one page with the release
 * detail view rather than a separate admin skin.
 */

/** Wraps list rows so every collection in every tab divides the same way. */
export function List({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-white/5">{children}</div>;
}

/**
 * The one row shape used by every collection on this page: a primary line, an
 * optional muted second line, and right-aligned metadata.
 */
export function ListRow({
  title,
  meta,
  trailing,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white/90">{title}</div>
        {meta && <div className="mt-0.5 text-xs text-neutral-500">{meta}</div>}
        {children}
      </div>
      {trailing && (
        <div className="flex shrink-0 items-center gap-3 text-xs leading-5 text-neutral-500">
          {trailing}
        </div>
      )}
    </div>
  );
}

/** A row that opens to reveal its full detail grid (releases, emails). */
export function ExpandableRow({
  summary,
  defaultOpen = false,
  children,
}: {
  summary: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="group -mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="min-w-0 flex-1">{summary}</div>
        <ExpandMoreIcon
          aria-hidden="true"
          className={cn(
            'size-4 shrink-0 fill-neutral-500 transition-transform group-hover:fill-neutral-300',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={panelId} className="mt-4 px-2 pb-2">
          {children}
        </div>
      )}
    </div>
  );
}

/** Neutral tag for plain values — locales, template names, categories. */
export function Chip({
  mono,
  children,
}: {
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border border-white/5 bg-white/[0.04] px-2 py-0.5 text-xs text-neutral-300',
        mono && 'font-mono',
      )}
    >
      {children}
    </span>
  );
}

/** On/off signal with a status dot instead of a coloured word. */
export function Flag({ on, children }: { on: boolean; children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span
        aria-hidden="true"
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          on ? 'bg-teal-400' : 'bg-neutral-600',
        )}
      />
      <span className={on ? 'text-white/90' : 'text-neutral-500'}>
        {children ?? (on ? 'Enabled' : 'Disabled')}
      </span>
    </span>
  );
}

const CALLOUT_TONES = {
  rose: 'border-rose-500/20 bg-rose-500/[0.06] text-rose-300',
  amber: 'border-amber-500/20 bg-amber-500/[0.06] text-amber-300',
} as const;

/** Inline failure/warning detail — rejection reasons, processing errors. */
export function Callout({
  tone = 'rose',
  label,
  children,
}: {
  tone?: keyof typeof CALLOUT_TONES;
  label?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-2 text-xs leading-relaxed',
        CALLOUT_TONES[tone],
      )}
    >
      {label && <span className="font-medium">{label} </span>}
      <span className="whitespace-pre-wrap text-neutral-300">{children}</span>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-6 text-center text-xs text-neutral-600">{message}</div>
  );
}

/** Muted em dash for absent values, so blanks stay visually consistent. */
export function Empty() {
  return <span className="text-neutral-600">—</span>;
}
